import React from 'react';
import { FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import store2 from 'store2';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { assembleUrl } from '../../store/assembleUrl';
import { fetchGet } from '../../store/fetch';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { appContext } from '../appContext';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSection,
    EditPanelSelect,
    PanelBody,
    PanelLink,
    PanelWithoutForm,
    RightTray,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    Topbar,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { IMaintenanceMeasure } from '../interfaces/IMaintenanceMeasure';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { ConditionScoreLabel } from '../utils/ConditionScoreLabel';
import { DisplayMessage } from '../utils/displayMessage';
import { FmecaRiskLabel } from '../utils/FmecaRiskLabel';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { RamsWeightedPrioLabel } from '../utils/RamsWeightedPrioLabel';

interface IValueText {
    value: string;
    text: string;
}

function FormatCondition(props: { score?: number }) {
    if (props === undefined) {
        return null;
    }
    if (props.score) {
        return <ConditionScoreLabel value={props.score} />;
    }
    return null;
}

function FormatRams(props: { ramsWeightedPriority?: string }) {
    if (props === undefined) {
        return null;
    }
    if (props.ramsWeightedPriority && props.ramsWeightedPriority !== '0') {
        return <RamsWeightedPrioLabel value={props.ramsWeightedPriority} />;
    }
    return null;
}

function FormatFmeca(props: {
    analysisRamsWeightedPriority?: string;
    verificationRamsWeightedPriority?: string;
    analysisRamsTotalPriority?: string;
    verificationRamsTotalPriority?: string;
}) {
    if (props === undefined) {
        return null;
    }
    if (props.verificationRamsWeightedPriority && props.verificationRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.verificationRamsWeightedPriority} totalValue={props.verificationRamsTotalPriority} />;
    } else if (props.analysisRamsWeightedPriority && props.analysisRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.analysisRamsWeightedPriority} totalValue={props.analysisRamsTotalPriority} />;
    }
    return null;
}
const storeKey = 'MaintenanceMeasuresList';
const itemsPerPage = 200;

interface IMaintenanceMeasuresListState {
    items: IMaintenanceMeasure[];
    totalItems: number;
    batches: IValueText[];
    contracts: IValueText[];
    tranches: IValueText[];
    objectTypes: IValueText[];
    batchId?: undefined | string;
    isLoading: boolean;
    searchParameters: {
        condition?: undefined | string;
        conditionCareScore?: undefined | string;
        searchText?: undefined | string;
        riskDefect?: undefined | string;
        page: number;
        riskFmeca?: undefined | string;
        inspectionStandardType?: undefined | string;
        batchId?: undefined | string;
        objectTypeId?: undefined | string;
        tranchId?: undefined | string;
        contractId?: undefined | string;
    };
}

interface IMaintenanceMeasuresListProps extends ITranslateProps, IRouterProps {
    languageCode: string;
    useBatches: boolean;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        useBatches: authentication.useBatches,
        languageCode: language.code,
    };
}

class MaintenanceMeasuresListComponent extends Component<IMaintenanceMeasuresListProps, IMaintenanceMeasuresListState> {
    constructor(props: IMaintenanceMeasuresListProps) {
        super(props);

        this.state = {
            items: [],
            totalItems: 0,
            batches: [],
            tranches: [],
            contracts: [],
            objectTypes: [],
            searchParameters: {
                page: 1,
            },
            isLoading: false,
        };
    }

    public override async componentDidMount() {
        if (store2.get(storeKey)) {
            await this.setState({ searchParameters: store2.get(storeKey) });
        }
        await this.search();
        if (this.props.useBatches) {
            await this.loadBatches();
        }
        await this.loadObjectTypes();
    }

    public override render() {
        return (
            <appContext.Consumer>
                {() => (
                    <div>
                        <Topbar>
                            <TopbarLeft>
                                <li className="crumb-active">
                                    <span className="fa fa-wrench pr10"></span>
                                    <a href="#">{this.props.t('maintenanceMeasure.openMaintenanceMeasures')}</a>
                                </li>
                            </TopbarLeft>
                            <TopbarRight>
                                <PanelLink
                                    href={assembleUrl('/api/export/miokExcelObject', this.getQueryParameters())}
                                    text="MJOP export"
                                    isDownload={true}
                                    icon="fa fa-download"
                                    small={true}
                                />
                            </TopbarRight>
                        </Topbar>
                        <Content>
                            <Tray>
                                {this.state.totalItems > 200 && (
                                    <div className="text-center">
                                        <DisplayMessage
                                            message={this.props.t('maintenanceMeasure.moreThan200Items', this.state.totalItems.toString())}
                                            messageType="general"
                                        />
                                    </div>
                                )}
                                <PanelWithoutForm>
                                    <PanelBody noPadding={true}>
                                        <div className="table-responsive">
                                            <table className="table table-hover fs13">
                                                <TableHead>
                                                    <tr className="bg-light">
                                                        <TableHeader>{this.props.t('common.object')}</TableHeader>
                                                        <TableHeader>{this.props.t('maintenanceMeasure.maintenanceMeasure')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.cost')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.year')}</TableHeader>
                                                        <TableHeader>{this.props.t('survey.surveyedOn')}</TableHeader>
                                                        <TableHeader align="right">{this.props.t('survey.riskDefect')}</TableHeader>
                                                        {isCustomerVersion('zxy') && (
                                                            <TableHeader align="right">{this.props.t('survey.riskFailure')}</TableHeader>
                                                        )}
                                                        <TableHeader align="right">{this.props.t('common.condition')}</TableHeader>
                                                    </tr>
                                                </TableHead>

                                                {(this.state.isLoading && (
                                                    <TableBody>
                                                        <TableRow>
                                                            <td colSpan={7} className="text-center">
                                                                <Spinner />
                                                            </td>
                                                        </TableRow>
                                                    </TableBody>
                                                )) || (
                                                    <TableBody>
                                                        {this.state.items &&
                                                            this.state.items.map((maintenance, i) => (
                                                                <TableRow key={i} onClick={() => this.rowClicked(maintenance.surveyId)}>
                                                                    <TableData>{maintenance.survey?.object?.name}</TableData>
                                                                    <TableData>
                                                                        {maintenance.description}
                                                                        {(maintenance.defaultMaintenanceMeasureId && (
                                                                            <div className="fs12 text-muted-darker">
                                                                                {maintenance.maintenanceType}{' '}
                                                                            </div>
                                                                        )) || (
                                                                            <div className="fs12 text-muted-darker">
                                                                                {this.props.t('maintenanceMeasure.correctiveMaintenance')}{' '}
                                                                            </div>
                                                                        )}
                                                                    </TableData>
                                                                    <TableData>
                                                                        <FormattedNumber
                                                                            value={maintenance.cost ?? 0}
                                                                            style="currency"
                                                                            currency="EUR"
                                                                        />{' '}
                                                                    </TableData>
                                                                    <TableData>
                                                                        {maintenance.planYear}{' '}
                                                                        {maintenance.finalPlanYear && <span>-{maintenance.finalPlanYear}</span>}
                                                                    </TableData>
                                                                    <TableData nowrap>
                                                                        <NullableFormattedDate value={maintenance.survey?.surveryedOn} />
                                                                    </TableData>
                                                                    <TableData alignRight={true}>
                                                                        <FormatRams {...maintenance.defect}></FormatRams>
                                                                    </TableData>
                                                                    {isCustomerVersion('zxy') && (
                                                                        <TableData alignRight={true}>
                                                                            <FormatFmeca {...maintenance.failureMode}></FormatFmeca>
                                                                        </TableData>
                                                                    )}
                                                                    <TableData alignRight={true}>
                                                                        <FormatCondition {...maintenance.defect}></FormatCondition>
                                                                    </TableData>
                                                                </TableRow>
                                                            ))}
                                                    </TableBody>
                                                )}
                                            </table>
                                        </div>
                                    </PanelBody>
                                </PanelWithoutForm>
                            </Tray>
                            <RightTray>
                                <form
                                    id="filter"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        void this.search();
                                    }}
                                >
                                    <div className="admin-form">
                                        <h4>{this.props.t('common.findItem', this.props.t('maintenanceMeasure.maintenanceMeasures'))}</h4>
                                        <EditPanelSection>
                                            <EditPanelInput
                                                name="searchText"
                                                placeholder={this.props.t('common.description')}
                                                value={this.state.searchParameters.searchText}
                                                icon="fa fa-wrench"
                                                handleChange={this.handleChange.bind(this)}
                                                handleEnter={this.search.bind(this)}
                                            />
                                        </EditPanelSection>
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="objectTypeId"
                                                value={this.state.searchParameters.objectTypeId}
                                                handleChange={this.handleChange.bind(this)}
                                                placeholder={this.props.t('common.allItems', this.props.t('common.objectType'))}
                                                options={this.state.objectTypes}
                                            />
                                        </EditPanelSection>
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="inspectionStandardType"
                                                value={this.state.searchParameters.inspectionStandardType}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.inspectionType'))}
                                                options={getInspectionStandardTypeOptions(this.props.t)}
                                            />
                                        </EditPanelSection>
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="riskDefect"
                                                value={this.state.searchParameters.riskDefect}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.selectItem', this.props.t('survey.riskDefect'))}
                                                options={[
                                                    { value: '4', text: 'Direct herstellen' },
                                                    { value: '3', text: 'Binnen 2 jaar herstellen' },
                                                    { value: '2', text: 'Binnen 5 jaar herstellen' },
                                                    { value: '1', text: 'Herstellen na 5 jaar' },
                                                ]}
                                            />
                                        </EditPanelSection>
                                        {isCustomerVersion('zxy') && (
                                            <EditPanelSection>
                                                <EditPanelSelect
                                                    name="riskFmeca"
                                                    value={this.state.searchParameters.riskFmeca}
                                                    handleChange={this.handleChange.bind(this)}
                                                    disabled={this.state.isLoading}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('survey.fmecaRisk'))}
                                                    options={[
                                                        { value: '1', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.1')) },
                                                        { value: '2', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.2')) },
                                                        { value: '3', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.3')) },
                                                        { value: '4', text: this.props.t('fmecaRisk.4') },
                                                    ]}
                                                />
                                            </EditPanelSection>
                                        )}
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="condition"
                                                value={this.state.searchParameters.condition}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.conditions'))}
                                                options={[
                                                    { value: '2', text: this.props.t('condition.orLess', this.props.t('condition.good')) },
                                                    { value: '3', text: this.props.t('condition.orLess', this.props.t('condition.reasonable')) },
                                                    { value: '4', text: this.props.t('condition.orLess', this.props.t('condition.moderate')) },
                                                    { value: '5', text: this.props.t('condition.orLess', this.props.t('condition.poor')) },
                                                    { value: '6', text: this.props.t('condition.bad') },
                                                ]}
                                            />
                                        </EditPanelSection>
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="conditionCareScore"
                                                value={this.state.searchParameters.conditionCareScore}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.care'))}
                                                options={[
                                                    { value: '2', text: this.props.t('condition.orLess', this.props.t('condition.good')) },
                                                    { value: '3', text: this.props.t('condition.orLess', this.props.t('condition.reasonable')) },
                                                    { value: '4', text: this.props.t('condition.orLess', this.props.t('condition.moderate')) },
                                                    { value: '5', text: this.props.t('condition.orLess', this.props.t('condition.poor')) },
                                                    { value: '6', text: this.props.t('condition.bad') },
                                                ]}
                                            />
                                        </EditPanelSection>
                                        {this.props.useBatches && (
                                            <EditPanelSection>
                                                <EditPanelFilterSelect
                                                    name="batchId"
                                                    value={this.state.searchParameters.batchId}
                                                    handleChange={this.handleChange.bind(this)}
                                                    disabled={this.state.isLoading}
                                                    placeholder={this.props.t('common.findItem', 'Batch')}
                                                    options={this.state.batches}
                                                />
                                            </EditPanelSection>
                                        )}
                                        <EditPanelButtons topBorder>
                                            <EditPanelButton
                                                onClick={async () => this.resetForm()}
                                                text={this.props.t('common.reset')}
                                                disabled={this.state.isLoading}
                                            />
                                            <EditPanelButton
                                                onClick={async () => this.search()}
                                                text={this.props.t('common.search')}
                                                primary
                                                disabled={this.state.isLoading}
                                            />
                                        </EditPanelButtons>
                                    </div>
                                </form>
                            </RightTray>
                        </Content>
                    </div>
                )}
            </appContext.Consumer>
        );
    }

    private async loadObjectTypes() {
        const result = await fetchGet('/api/objectTypesAvailable', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ objectTypes: valueTextArray });
        }
    }

    // private async loadContracts() {
    //     const result = await fetchGet('/api/contractsList', {}, ['NO_DATA_AVAILABLE']);
    //     const items = result.json;
    //     if (items && items.length > 0) {
    //         const valueTextArray = items.map((item: any) => { return { value: item.id, text: item.name }; });
    //         await this.setState({ contracts: valueTextArray });
    //     }
    // }

    // private async loadTranches() {
    //     const result = await fetchGet('/api/tranchesList', { contractId: this.state.searchParameters.contractId || undefined }, ['NO_DATA_AVAILABLE']);
    //     const items = result.json;
    //     if (items && items.length > 0) {
    //         const valueTextArray = items.map((item: any) => { return { value: item.id, text: item.name }; });
    //         await this.setState({ tranches: valueTextArray });
    //     }
    // }

    private async loadBatches() {
        const queryParamters = {} as { [key: string]: string };
        // if (this.state.searchParameters.contractId) {
        //     queryParamters.contractId = this.state.searchParameters.contractId;
        // }
        // if (this.state.searchParameters.tranchId) {
        //     queryParamters.tranchId = this.state.searchParameters.tranchId;
        // }
        const result = await fetchGet('/api/batcheslist', queryParamters, [][403]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({
                value: item.id,
                text: `${item.name.substring(0, 42)}${item.name.length > 43 ? '...' : ''}`,
            }));
            await this.setState({ batches: valueTextArray });
        } else {
            await this.setState({ batches: [] });
        }
    }

    private rowClicked(surveyId: string) {
        const url = `/surveys/${surveyId}`;
        this.props.navigate(url);
    }

    private async handleChange(value: any, stateKey: string) {
        const newState: any = this.state.searchParameters;
        newState[stateKey] = value;
        await this.setState({ searchParameters: newState });

        if (stateKey !== 'searchText' && stateKey !== 'tranchId' && stateKey !== 'contractId') {
            await this.search();
        }
        if (stateKey === 'contractId') {
            await this.loadBatches();
            // await this.loadTranches();
        }
        if (stateKey === 'tranchId') {
            await this.loadBatches();
        }
    }

    private async resetForm() {
        await this.setState({
            searchParameters: {
                ...this.state.searchParameters,
                ...{
                    condition: undefined,
                    conditionCareScore: undefined,
                    searchText: undefined,
                    riskDefect: undefined,
                    riskConstruction: undefined,
                    riskFmeca: undefined,
                    inspectionStandardType: undefined,
                    batchId: undefined,
                    objectTypeId: undefined,
                    contractId: undefined,
                    tranchId: undefined,
                },
            },
        });
        await this.search();
    }

    private async search(pageString?: string) {
        const pageNumber = pageString ? parseInt(pageString, 10) : 1;
        await this.setState({ isLoading: true, searchParameters: { ...this.state.searchParameters, ...{ page: pageNumber } } });

        store2.set(storeKey, this.state.searchParameters);

        const queryParamters = this.getQueryParameters();
        queryParamters.limit = itemsPerPage.toString();
        queryParamters.offset = (itemsPerPage * (pageNumber - 1)).toString();

        const result = await fetchGet('/api/maintenanceMeasures', queryParamters);
        await this.setState({ items: result.json.items, totalItems: result.json.total, isLoading: false });
    }

    private getQueryParameters(): { [key: string]: string } {
        return {
            condition: this.state.searchParameters.condition ? this.state.searchParameters.condition : '',
            conditionCareScore: this.state.searchParameters.conditionCareScore ? this.state.searchParameters.conditionCareScore : '',
            searchText: this.state.searchParameters.searchText ? this.state.searchParameters.searchText : '',
            riskdefect: this.state.searchParameters.riskDefect ? this.state.searchParameters.riskDefect : '',
            riskfmeca: this.state.searchParameters.riskFmeca ? this.state.searchParameters.riskFmeca : '',
            batchId: this.state.searchParameters.batchId ? this.state.searchParameters.batchId : '',
            inspectionStandardType: this.state.searchParameters.inspectionStandardType ? this.state.searchParameters.inspectionStandardType : '',
            objectTypeId: this.state.searchParameters.objectTypeId ? this.state.searchParameters.objectTypeId : '',
        };
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MaintenanceMeasuresList = translate()(
    connect(mapStateToProps, undefined, undefined, { forwardRef: true })(MaintenanceMeasuresListComponent),
);
